import React from 'react';
import { Paper, List, ListItemButton, ListItemIcon, ListItemText, Divider, Tooltip, Box } from '@mui/material';
import { PictureAsPdf as PdfIcon, Quiz as QuizIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import ReadingSignsHelp from './ReadingSignsHelp';
import DonateButton from './DonateButton';

// Drapeaux en SVG inline plutot qu'emoji : les emoji drapeau (regional indicator
// symbols) ne sont pas rendus comme des images sur Windows par defaut (police
// systeme sans emoji couleur pour ce bloc) — ils s'affichent en code pays texte
// ("FR", "GB"...) au lieu du drapeau. Le SVG est independant de l'OS/police.
const FlagFR = (props) => (
  <svg viewBox="0 0 3 2" {...props}>
    <rect width="1" height="2" x="0" fill="#0055A4" />
    <rect width="1" height="2" x="1" fill="#FFFFFF" />
    <rect width="1" height="2" x="2" fill="#EF4135" />
  </svg>
);

const FlagGB = (props) => (
  <svg viewBox="0 0 60 40" {...props}>
    <rect width="60" height="40" fill="#00247d" />
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#cf142b" strokeWidth="2" />
    <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0 V40 M0,20 H60" stroke="#cf142b" strokeWidth="6" />
  </svg>
);

const FlagES = (props) => (
  <svg viewBox="0 0 3 2" {...props}>
    <rect width="3" height="2" fill="#AA151B" />
    <rect width="3" height="1" y="0.5" fill="#F1BF00" />
  </svg>
);

const FlagDE = (props) => (
  <svg viewBox="0 0 3 2" {...props}>
    <rect width="3" height="2" fill="#000000" />
    <rect width="3" height="1.333" fill="#DD0000" />
    <rect width="3" height="0.667" fill="#FFCE00" />
  </svg>
);

const FLAGS = { fr: FlagFR, en: FlagGB, es: FlagES, de: FlagDE };

function Flag({ country, muted }) {
  const Component = FLAGS[country];
  return (
    <Box
      sx={{
        width: 26,
        height: 19,
        borderRadius: '3px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        opacity: muted ? 0.45 : 1,
        filter: muted ? 'grayscale(1)' : 'none',
      }}
    >
      <Component width="100%" height="100%" preserveAspectRatio="xMidYMid slice" style={{ display: 'block' }} />
    </Box>
  );
}

const LANGUAGES = [
  { code: 'fr', label: 'Duala-Français', available: true },
  { code: 'en', label: 'Duala-Anglais', available: true },
  { code: 'es', label: 'Duala-Espagnol', available: true },
  { code: 'de', label: 'Duala-Allemand', available: true },
];

// Source publique originale (domaine public, E. Dinkelacker 1914) — on pointe vers
// archive.org plutôt que d'héberger notre propre copie du PDF (15,8 Mo).
const PDF_URL = 'https://archive.org/download/wrterbuchderdu00dinkuoft/wrterbuchderdu00dinkuoft.pdf';

function DictionaryLanguageSidebar({ language, onChange }) {
  return (
    <Paper variant="outlined" sx={{ p: 1, width: { xs: '100%', sm: 220 }, flexShrink: 0, height: 'fit-content' }}>
      <List dense>
        {LANGUAGES.map((lang) => (
          <Tooltip
            key={lang.code}
            title={lang.available ? '' : 'Bientôt disponible'}
            arrow
            disableHoverListener={lang.available}
          >
            <Box component="li" sx={{ listStyle: 'none' }}>
              <ListItemButton
                selected={language === lang.code}
                disabled={!lang.available}
                onClick={() => lang.available && onChange(lang.code)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Flag country={lang.code} muted={!lang.available} />
                </ListItemIcon>
                <ListItemText primary={lang.label} />
              </ListItemButton>
            </Box>
          </Tooltip>
        ))}
      </List>

      <Divider sx={{ my: 1 }} />

      <Tooltip title="Ouvre le PDF original sur archive.org" arrow describeChild>
        <ListItemButton
          component="a"
          href={PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <PdfIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Télécharger le PDF source" secondary="Dinkelacker, 1914" />
        </ListItemButton>
      </Tooltip>

      <ListItemButton component={RouterLink} to="/quiz" sx={{ borderRadius: 1 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <QuizIcon color="warning" />
        </ListItemIcon>
        <ListItemText primary={{ fr: 'Jouer au quiz', en: 'Play the quiz', es: 'Jugar al quiz', de: 'Quiz spielen' }[language] || 'Quiz'} />
      </ListItemButton>

      <ReadingSignsHelp language={language} />

      <Box sx={{ px: 1, pt: 1, pb: 0.5 }}><DonateButton language={language} fullWidth /></Box>
    </Paper>
  );
}

export default DictionaryLanguageSidebar;
